import React, { useState } from 'react';
import { Briefcase } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { mockProjects } from '@/data/projectsData';
import { ProjectDetailsDialog } from '@/components/Pages/ActiveProjects/ProjectDetailsDialog';
import { ProjectStatsCards } from '@/components/Pages/ActiveProjects/ProjectStatsCards';
import { ProjectFilterBar } from '@/components/Pages/ActiveProjects/ProjectFilterBar';
import { ProjectsView } from '@/components/Pages/ActiveProjects/ProjectsView';
import { ProjectManagementModal } from '@/components/Pages/ActiveProjects/ProjectManagementModal';
import { useActiveProjects } from '@/components/Pages/ActiveProjects/useActiveProjects';

export const ActiveProjectsPage: React.FC = () => {
  const {
    searchQuery,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    sortBy,
    setSortBy,
    selectedProject,
    showDetailsDialog,
    setShowDetailsDialog,
    projectCostCentres,
    setProjectCostCentres,
    isLoadingCostCentres,
    isLoadingTeamMembers,
    pagination,
    isLoading,
    isLoadingStats,
    displayProjects,
    displayStats,
    activeMetric,
    projectIdFilter,
    setProjectIdFilter,
    hasActiveFilters,
    getEmptyStateType,
    screenMode,
    handleSearchChange,
    handleClearSearch,
    handlePageChange,
    handleMetricCardClick,
    handleViewDetails,
    handleClearAllFilters,
    handleExportProjects,
  } = useActiveProjects();

  // Purely presentational state kept in the page; the hook owns data/filters.
  const [showProjectManagementModal, setShowProjectManagementModal] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'cards'>('cards');

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Active Projects"

        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<Briefcase />}
      />

      {/* Stats Cards */}
      <ProjectStatsCards
        stats={displayStats}
        isLoadingStats={isLoadingStats}
        activeMetric={activeMetric}
        onMetricClick={handleMetricCardClick}
      />

      {/* Enhanced Filters and Search */}
      <ProjectFilterBar
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        priorityFilter={priorityFilter}
        sortBy={sortBy}
        onSearchChange={handleSearchChange}
        onStatusFilterChange={setStatusFilter}
        onPriorityFilterChange={setPriorityFilter}
        onSortByChange={setSortBy}
        onClearSearch={handleClearSearch}
        onClearAllFilters={handleClearAllFilters}
        hasActiveFilters={hasActiveFilters}
        projectIdFilter={projectIdFilter}
        onClearProjectIdFilter={() => setProjectIdFilter(null)}
        displayProjects={displayProjects}
        screenMode={screenMode}
        pagination={pagination}
        totalMockProjects={mockProjects.length}
      />

      {/* Projects View with Toggle and Export */}
      <ProjectsView
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onExport={handleExportProjects}
        isLoading={isLoading}
        displayProjects={displayProjects}
        onViewDetails={handleViewDetails}
        screenMode={screenMode}
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyStateType={getEmptyStateType()}
        searchQuery={searchQuery}
        onClearSearch={handleClearSearch}
        onClearAllFilters={handleClearAllFilters}
      />

      {/* Project Details Dialog */}
      {selectedProject && (
        <ProjectDetailsDialog
          project={selectedProject}
          open={showDetailsDialog}
          onOpenChange={(open) => {
            setShowDetailsDialog(open);
            if (!open) {
              setProjectCostCentres([]);
            }
          }}
          screenMode={screenMode}
          costCentres={projectCostCentres}
          isLoadingCostCentres={isLoadingCostCentres}
          isLoadingTeamMembers={isLoadingTeamMembers}
        />
      )}

      {/* Project Management Application Modal */}
      <ProjectManagementModal
        open={showProjectManagementModal}
        onOpenChange={setShowProjectManagementModal}
      />
    </div>
  );
};
